import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Collapse, IconButton, InputAdornment, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp, Save } from '@mui/icons-material';
import { motion } from 'framer-motion';
import api from '../../../services/api';
import type { CostClose, CostCloseRow, FactoryResults, ProductProfit } from '../../../types/factory';
import { EmptyState, KpiItem, KpiStrip, PageHeader, Section, StatusPill, TONES, Tone } from '../../../components/ui/PageKit';
import { errorMessage, fmtMoney, fmtMoney2, fmtQty, num, todayIso } from '../shared';
import { ACCENT, ACCENT_SOFT, AnimatedNumber, INK, MUTED } from '../plant/ui';

// Ex ante vs ex post. Ex ante = el costo estándar que se calcula antes para fijar precios (la pantalla Costeo).
// Ex post = lo que costó de verdad cada mes, con los gastos y la producción reales. La diferencia son los desvíos.
// Arriba, "dónde estamos": ventas, fijos, contribución y equilibrio; abajo, qué producto deja más por hora de
// máquina y un simulador para decidir si conviene cambiar la matriz.

const monthLabel = (p: string, long = false) => {
  const s = new Date(`${p}-15T12:00:00`).toLocaleDateString('es-AR', long ? { month: 'long', year: 'numeric' } : { month: 'short', year: '2-digit' }).replace('.', '');
  return s.charAt(0).toUpperCase() + s.slice(1);
};
const lastMonths = (n: number) => {
  const t = new Date(`${todayIso()}T12:00:00`);
  return Array.from({ length: n }, (_, i) => {
    const d = new Date(t.getFullYear(), t.getMonth() - i, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
};
const pct = (v?: number | null, d = 1) => (v == null ? '—' : `${(v * 100).toFixed(d).replace('.', ',')}%`);
const signed = (v: number) => `${v > 0 ? '+' : v < 0 ? '−' : ''}${fmtMoney2(Math.abs(v))}`;
const toneOf = (v?: number | null): Tone => (v == null ? 'neutral' : v < 0 ? 'bad' : 'ok');

const FactoryCostClosePage: React.FC = () => {
  const [results, setResults] = useState<FactoryResults | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api.getFactoryResults({ months: 12, productMonths: 3 }).then(setResults).catch(e => setError(errorMessage(e, 'No se pudo calcular el resultado')));
  }, []);

  return (
    <>
      <PageHeader
        title="Costo real vs costo estándar"
        description="El ex ante es el costo que se calcula antes para poner precios (Costeo). El ex post es lo que costó de verdad cada mes. La diferencia muestra dónde se gana o se pierde plata: precio de la materia prima, rotos y horas de máquina, o pocas unidades para repartir los gastos fijos."
      />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <WhereWeAre results={results} />
      <Concepts results={results} />
      <MonthClose />
      <Profitability results={results} />
      <MoldSimulator results={results} />
    </>
  );
};

// ==================== Dónde estamos hoy ====================

const WhereWeAre: React.FC<{ results: FactoryResults | null }> = ({ results }) => {
  const months = results?.months ?? [];
  const withSales = months.filter(m => m.salesTotal > 0);
  const last = withSales[withSales.length - 1];
  const max = Math.max(1, ...months.flatMap(m => [m.salesTotal, m.fixed]));
  return (
    <Section title="Dónde estamos hoy" description="Ventas (con y sin factura), gastos fijos y resultado estimado de cada mes. La contribución sale de las ventas con detalle de producto y su costo variable.">
      {!results ? <Box sx={{ height: 160 }} /> : !last ? (
        <EmptyState title="Todavía no hay ventas cargadas" hint="Se llena con el libro IVA y las cuentas corrientes." />
      ) : (
        <Box sx={{ p: 2.5 }}>
          <KpiStrip>
            <KpiItem label={`Ventas · ${monthLabel(last.month, true)}`} value={<AnimatedNumber value={last.salesTotal} prefix="$" />}
              hint={`${fmtMoney(last.salesInvoiced)} con factura · ${fmtMoney(last.salesAccount)} sin`} />
            <KpiItem label="Contribución" value={last.contribution != null ? <AnimatedNumber value={last.contribution} prefix="$" /> : '—'} hint={`${pct(last.contributionRatio)} de cada peso vendido`} />
            <KpiItem label="Gastos fijos" value={<AnimatedNumber value={last.fixed} prefix="$" />} hint={last.fixedSource === 'cierre' ? 'del cierre del mes' : 'estándar del Costeo'} />
            <KpiItem label="Resultado estimado" value={last.result != null ? <AnimatedNumber value={last.result} prefix={last.result < 0 ? '−$' : '$'} /> : '—'}
              tone={toneOf(last.result)} hint={last.result != null && last.result < 0 ? 'pérdida del mes' : 'ganancia del mes'} />
            <KpiItem label="Punto de equilibrio" value={results.breakEvenSales != null ? <AnimatedNumber value={results.breakEvenSales} prefix="$" /> : '—'}
              hint="ventas por mes para cubrir los fijos" />
          </KpiStrip>
          <Typography sx={{ fontWeight: 700, mb: 1 }}>Últimos 12 meses</Typography>
          <Stack direction="row" spacing={1} alignItems="flex-end" sx={{ height: 190, overflowX: 'auto', pb: 0.5 }}>
            {months.map((m, i) => (
              <Stack key={m.month} alignItems="center" sx={{ flex: 1, minWidth: 46, height: '100%' }}>
                <Box sx={{ flex: 1, width: '100%', position: 'relative', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: '3px' }}>
                  <motion.div title={`Ventas ${fmtMoney(m.salesTotal)}`} style={{ width: '42%', borderRadius: 4, background: ACCENT }}
                    initial={{ height: 0 }} animate={{ height: `${(m.salesTotal / max) * 100}%` }} transition={{ duration: 0.7, delay: i * 0.04 }} />
                  <motion.div title={`Fijos ${fmtMoney(m.fixed)}`} style={{ width: '42%', borderRadius: 4, background: '#C9CDD4' }}
                    initial={{ height: 0 }} animate={{ height: `${(m.fixed / max) * 100}%` }} transition={{ duration: 0.7, delay: i * 0.04 + 0.05 }} />
                </Box>
                <Typography sx={{ fontSize: 10.5, color: MUTED, mt: 0.5 }}>{monthLabel(m.month)}</Typography>
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: m.result == null ? MUTED : m.result < 0 ? TONES.bad.fg : TONES.ok.fg }}>
                  {m.result == null ? '—' : `${m.result < 0 ? '−' : '+'}${(Math.abs(m.result) / 1e6).toFixed(1).replace('.', ',')}M`}
                </Typography>
              </Stack>
            ))}
          </Stack>
          <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
            <Legend color={ACCENT} label="Ventas" />
            <Legend color="#C9CDD4" label="Gastos fijos" />
            <Typography variant="caption" color="text.secondary">Abajo: resultado estimado (contribución − fijos), en millones.</Typography>
          </Stack>
        </Box>
      )}
    </Section>
  );
};

const Legend: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <Stack direction="row" spacing={0.75} alignItems="center"><Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: color }} /><Typography variant="caption">{label}</Typography></Stack>
);

// ==================== Cómo se lee ====================

const Concepts: React.FC<{ results: FactoryResults | null }> = ({ results }) => (
  <Section title="Cómo se lee" description="Tres ideas para entender el costo de una fábrica.">
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2, p: 2.5 }}>
      <Concept n="1" title="Ex ante: el costo que se calcula antes" body="Supone cuántas unidades por hora hace cada máquina, cuánta merma hay y cuántas horas anda la planta. Sirve para poner el precio. Es la pantalla Costeo." />
      <Concept n="2" title="Ex post: lo que costó de verdad" body="Al cierre del mes: gastos reales de MO y estructura, materia prima al precio que se pagó y lo que realmente salió de las máquinas, con sus rotos. Sirve para controlar." />
      <Concept n="3" title="Desvíos: por qué no coinciden" body="Precio de la MP (se pagó más o menos), eficiencia (más rotos o más horas por unidad) y volumen (los gastos fijos se repartieron en menos unidades de las previstas)." />
    </Box>
    <Box sx={{ mx: 2.5, mb: 2.5, p: 2.5, borderRadius: 3, bgcolor: ACCENT_SOFT }}>
      <Typography sx={{ fontWeight: 800, fontSize: 17, color: INK }}>Los fijos se pagan por tiempo; los variables, por unidad</Typography>
      <Typography sx={{ mt: 0.75, color: '#5B4A40', maxWidth: '90ch' }}>
        Sueldos, luz, alquiler: se pagan igual si la máquina anda o está parada. La materia prima se gasta solo cuando se produce. Por eso
        <b> las horas que anda cada máquina son las que convierten un costo fijo en costo por unidad</b>: si una inyectora anda la mitad de las
        horas previstas, cada balde carga el doble de sueldos y de estructura, aunque la MP sea la misma.
        {results ? ` Hoy cada hora de planta cuesta ${fmtMoney(results.fixedHourCost)} de fijos, ande o no la máquina.` : ''}
      </Typography>
    </Box>
  </Section>
);

const Concept: React.FC<{ n: string; title: string; body: string }> = ({ n, title, body }) => (
  <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
    <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: ACCENT, color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 800, mb: 1 }}>{n}</Box>
    <Typography sx={{ fontWeight: 750 }}>{title}</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{body}</Typography>
  </Paper>
);

// ==================== Cierre del mes ====================

const MonthClose: React.FC = () => {
  const months = useMemo(() => lastMonths(12), []);
  const [period, setPeriod] = useState(months[1]);
  const [close, setClose] = useState<CostClose | null>(null);
  const [form, setForm] = useState({ labor: '', structure: '', hours: '', notes: '' });
  const [prices, setPrices] = useState<Record<number, string>>({});
  const [manual, setManual] = useState<Record<number, { good: string; scrap: string; hours: string }>>({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const [open, setOpen] = useState<number | null>(null);

  const load = (c: CostClose) => {
    setClose(c);
    setForm({ labor: c.laborActual?.toString() ?? '', structure: c.structureActual?.toString() ?? '', hours: c.laborHoursPaid?.toString() ?? '', notes: c.notes ?? '' });
    setPrices(Object.fromEntries(c.materialPrices.map(m => [m.materialId, m.price?.toString() ?? ''])));
    setManual(Object.fromEntries(c.manualProduction.map(m => [m.productId, { good: String(m.goodQuantity), scrap: String(m.scrapQuantity), hours: m.hours?.toString() ?? '' }])));
  };
  useEffect(() => {
    setClose(null);
    setMsg(null);
    api.getCostClose(period).then(load).catch(e => setMsg({ tone: 'error', text: errorMessage(e, 'No se pudo traer el cierre') }));
  }, [period]);

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const n = (s: string) => (s.trim() === '' ? null : num(s));
      const c = await api.saveCostClose(period, {
        laborActual: n(form.labor), structureActual: n(form.structure), laborHoursPaid: n(form.hours), notes: form.notes || null,
        materialPrices: Object.entries(prices).filter(([, v]) => v.trim() !== '').map(([id, v]) => ({ materialId: Number(id), standardPrice: 0, price: num(v) })),
        manualProduction: Object.entries(manual).filter(([, v]) => num(v.good) > 0)
          .map(([id, v]) => ({ productId: Number(id), goodQuantity: num(v.good), scrapQuantity: num(v.scrap), hours: v.hours.trim() === '' ? null : num(v.hours) })),
      });
      load(c);
      setMsg({ tone: 'success', text: 'Cierre guardado. El ex post ya usa estos números.' });
    } catch (e) {
      setMsg({ tone: 'error', text: errorMessage(e, 'No se pudo guardar') });
    } finally {
      setSaving(false);
    }
  };

  const t = close?.totals;
  const diff = t ? t.actualCost - t.standardCost : 0;
  return (
    <Section
      title="Cierre del mes: ex ante vs ex post"
      description="Cargá lo que realmente se gastó en el mes. La producción y las horas de máquina salen de los partes; si un producto no tiene partes, cargalo a mano."
      actions={(
        <TextField select size="small" label="Mes" value={period} onChange={e => setPeriod(e.target.value)} sx={{ minWidth: 170 }}>
          {months.map(m => <MenuItem key={m} value={m}>{monthLabel(m, true)}</MenuItem>)}
        </TextField>
      )}
    >
      {!close ? <Box sx={{ height: 140 }} /> : (
        <Box sx={{ p: 2.5 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
            <MoneyField label="MO real del mes" value={form.labor} onChange={v => setForm({ ...form, labor: v })} hint={`estándar ${fmtMoney(close.standard.laborMonthly)}`} />
            <MoneyField label="Estructura real del mes" value={form.structure} onChange={v => setForm({ ...form, structure: v })} hint={`estándar ${fmtMoney(close.standard.structureMonthly)}`} />
            <TextField size="small" label="Horas de MO pagadas" value={form.hours} onChange={e => setForm({ ...form, hours: e.target.value })}
              helperText={`el Costeo supone ${fmtQty(close.standard.laborHoursMonthly)} h (${fmtQty(close.standard.plantHoursMonthly)} h de planta × ${close.standard.employees} personas)`} />
          </Box>
          {close.laborHourCostPaid != null && (
            <Alert severity={close.laborHourCostPaid > close.standard.laborHourCost * 1.2 ? 'warning' : 'info'} sx={{ mt: 2 }}>
              La hora de MO real cuesta <b>{fmtMoney(close.laborHourCostPaid)}</b>; el Costeo usa {fmtMoney(close.standard.laborHourCost)}.
              {close.laborHourCostPaid > close.standard.laborHourCost * 1.2 && ' El Costeo reparte la MO en más horas de las que se pagan, así que los productos salen más baratos de lo que son.'}
            </Alert>
          )}

          {close.materialPrices.length > 0 && (
            <>
              <Typography sx={{ fontWeight: 700, mt: 3, mb: 1 }}>Precio real de la materia prima</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 1.5 }}>
                {close.materialPrices.map(m => (
                  <MoneyField key={m.materialId} label={m.name ?? `MP ${m.materialId}`} value={prices[m.materialId] ?? ''}
                    onChange={v => setPrices({ ...prices, [m.materialId]: v })} hint={`lista ${fmtMoney2(m.standardPrice)}`} />
                ))}
              </Box>
            </>
          )}

          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 2.5 }}>
            <Button variant="contained" startIcon={<Save />} onClick={save} disabled={saving} sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#C9420F' }, textTransform: 'none', fontWeight: 700 }}>
              Guardar cierre de {monthLabel(period, true).toLowerCase()}
            </Button>
            <Typography variant="body2" color="text.secondary">
              {close.shiftEntries} partes en el mes · {fmtQty(close.machineHours)} h de máquina{!close.saved && ' · sin cierre cargado: el ex post usa los gastos estándar'}
            </Typography>
          </Stack>
          {msg && <Alert severity={msg.tone} sx={{ mt: 2 }}>{msg.text}</Alert>}

          {t && t.standardCost > 0 && (
            <Box sx={{ mt: 3 }}>
              <KpiStrip>
                <KpiItem label="Lo producido, a costo estándar" value={fmtMoney(t.standardCost)} />
                <KpiItem label="Lo producido, a costo real" value={fmtMoney(t.actualCost)} tone={diff > 0 ? 'bad' : 'ok'} hint={`${diff > 0 ? 'costó' : 'ahorró'} ${fmtMoney(Math.abs(diff))} ${diff > 0 ? 'de más' : ''}`} />
                <KpiItem label="Precio de MP" value={signed(t.materialPrice)} tone={t.materialPrice > 0 ? 'bad' : 'ok'} />
                <KpiItem label="Eficiencia (rotos y horas)" value={signed(t.materialEfficiency + t.laborEfficiency)} tone={t.materialEfficiency + t.laborEfficiency > 0 ? 'bad' : 'ok'} />
                <KpiItem label="Volumen (fijos en pocas unidades)" value={signed(t.volume)} tone={t.volume > 0 ? 'bad' : 'ok'} />
              </KpiStrip>
            </Box>
          )}

          <Table size="small" sx={{ mt: 1 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 36 }} />
                <TableCell>Producto</TableCell>
                <TableCell align="right">Producido</TableCell>
                <TableCell align="right">Uso de capacidad</TableCell>
                <TableCell align="right">Ex ante</TableCell>
                <TableCell align="right">Ex post</TableCell>
                <TableCell align="right">Diferencia</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {close.rows.map(r => (
                <CloseRow key={r.productId} row={r} open={open === r.productId} onToggle={() => setOpen(open === r.productId ? null : r.productId)}
                  manual={manual[r.productId]} onManual={v => setManual({ ...manual, [r.productId]: v })} />
              ))}
            </TableBody>
          </Table>
          {close.withoutCosting.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Con producción pero sin costeo (sus horas sí reparten la MO): {close.withoutCosting.join(', ')}.
            </Typography>
          )}
        </Box>
      )}
    </Section>
  );
};

const MoneyField: React.FC<{ label: string; value: string; onChange: (v: string) => void; hint?: string }> = ({ label, value, onChange, hint }) => (
  <TextField size="small" label={label} value={value} onChange={e => onChange(e.target.value)} helperText={hint}
    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
);

const VARIANCE_LABELS: { key: keyof Omit<NonNullable<CostCloseRow['variances']>, 'total'>; label: string; hint: string }[] = [
  { key: 'materialPrice', label: 'Precio de MP', hint: 'la materia prima se pagó más cara o más barata que la de lista' },
  { key: 'materialEfficiency', label: 'Rotos y merma', hint: 'más o menos material tirado que el previsto' },
  { key: 'laborEfficiency', label: 'Horas por unidad', hint: 'la máquina tardó más o menos que el estándar' },
  { key: 'volume', label: 'Volumen', hint: 'los fijos se repartieron en menos (o más) unidades que las previstas' },
];

const CloseRow: React.FC<{
  row: CostCloseRow; open: boolean; onToggle: () => void;
  manual?: { good: string; scrap: string; hours: string }; onManual: (v: { good: string; scrap: string; hours: string }) => void;
}> = ({ row, open, onToggle, manual, onManual }) => {
  const diff = row.exPost ? row.exPost.total - row.exAnte.total : null;
  const use = row.capacityUsePct;
  return (
    <>
      <TableRow hover onClick={onToggle} sx={{ cursor: 'pointer', '& > td': { borderBottom: open ? 0 : undefined } }}>
        <TableCell><IconButton size="small">{open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}</IconButton></TableCell>
        <TableCell sx={{ fontWeight: 650 }}>
          {row.productName}
          {row.source !== 'partes' && <Typography component="span" variant="caption" color="text.secondary"> · {row.source}</Typography>}
        </TableCell>
        <TableCell align="right">{row.goodQuantity ? `${fmtQty(row.goodQuantity)} u.` : '—'}{row.scrapQuantity > 0 && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{fmtQty(row.scrapQuantity)} rotos</Typography>}</TableCell>
        <TableCell align="right">{use == null ? '—' : <StatusPill tone={use >= 0.85 ? 'ok' : use >= 0.5 ? 'warn' : 'bad'}>{pct(use, 0)}</StatusPill>}</TableCell>
        <TableCell align="right">{fmtMoney2(row.exAnte.total)}</TableCell>
        <TableCell align="right" sx={{ fontWeight: 700 }}>{row.exPost ? fmtMoney2(row.exPost.total) : '—'}</TableCell>
        <TableCell align="right">{diff == null ? '—' : <StatusPill tone={diff > 0 ? 'bad' : 'ok'}>{signed(diff)}</StatusPill>}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={7} sx={{ p: 0 }}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, p: 2.5, bgcolor: '#FBFAF8' }}>
              <Box>
                <Typography sx={{ fontWeight: 700, mb: 1 }}>Costo por unidad</Typography>
                <Table size="small">
                  <TableHead><TableRow><TableCell /><TableCell align="right">Ex ante</TableCell><TableCell align="right">Ex post</TableCell></TableRow></TableHead>
                  <TableBody>
                    {([['Materia prima', 'material'], ['Mano de obra', 'labor'], ['Estructura', 'structure'], ['Armado', 'assembly']] as const).map(([l, k]) => (
                      <TableRow key={k}><TableCell>{l}</TableCell><TableCell align="right">{fmtMoney2(row.exAnte[k])}</TableCell><TableCell align="right">{row.exPost ? fmtMoney2(row.exPost[k]) : '—'}</TableCell></TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  {row.capacityUnits > 0 && `El ex ante supone ${fmtQty(row.capacityUnits)} u. por mes. `}
                  {row.hours > 0 && `Horas de máquina: ${fmtQty(row.hours)}${row.hoursEstimated ? ' (en parte estimadas por el estándar)' : ''}.`}
                </Typography>
              </Box>
              <Box>
                {row.variances ? (
                  <>
                    <Typography sx={{ fontWeight: 700, mb: 1 }}>Por qué cambió (por unidad)</Typography>
                    <Stack spacing={1}>
                      {VARIANCE_LABELS.map(v => {
                        const val = row.variances![v.key];
                        return (
                          <Stack key={v.key} direction="row" spacing={1.5} alignItems="baseline">
                            <Box sx={{ flex: 1 }}>
                              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{v.label}</Typography>
                              <Typography variant="caption" color="text.secondary">{v.hint}</Typography>
                            </Box>
                            <Typography sx={{ fontWeight: 750, color: Math.abs(val) < 0.005 ? MUTED : val > 0 ? TONES.bad.fg : TONES.ok.fg, whiteSpace: 'nowrap' }}>{signed(val)}</Typography>
                          </Stack>
                        );
                      })}
                    </Stack>
                  </>
                ) : (
                  <>
                    <Typography sx={{ fontWeight: 700, mb: 1 }}>Sin partes en el mes: cargalo a mano</Typography>
                    <Stack direction="row" spacing={1}>
                      <TextField size="small" label="Buenas" value={manual?.good ?? ''} onChange={e => onManual({ good: e.target.value, scrap: manual?.scrap ?? '', hours: manual?.hours ?? '' })} />
                      <TextField size="small" label="Rotos" value={manual?.scrap ?? ''} onChange={e => onManual({ good: manual?.good ?? '', scrap: e.target.value, hours: manual?.hours ?? '' })} />
                      <TextField size="small" label="Horas" value={manual?.hours ?? ''} onChange={e => onManual({ good: manual?.good ?? '', scrap: manual?.scrap ?? '', hours: e.target.value })} />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>Se guarda con "Guardar cierre". Si dejás las horas vacías se estiman con el estándar.</Typography>
                  </>
                )}
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// ==================== Rentabilidad por producto ====================

const Profitability: React.FC<{ results: FactoryResults | null }> = ({ results }) => {
  const rows = (results?.products ?? []).filter(p => p.contributionUnit != null || p.units > 0);
  const maxH = Math.max(1, ...rows.map(r => r.contributionPerHour ?? 0));
  return (
    <Section
      title="Qué producto deja más"
      description={results ? `Ventas de ${monthLabel(results.productsFrom, true).toLowerCase()} a ${monthLabel(results.productsTo, true).toLowerCase()}. Contribución = precio − materia prima y armado (lo que cuesta de más cada unidad). Margen = precio − costo completo. Ordenado por contribución por hora de máquina, que es lo que importa cuando las horas de máquina son el límite.` : undefined}
    >
      {!results ? <Box sx={{ height: 120 }} /> : rows.length === 0 ? <EmptyState title="Sin ventas con detalle de producto ni costeo" /> : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Producto</TableCell>
              <TableCell align="right">Vendido</TableCell>
              <TableCell align="right">Precio</TableCell>
              <TableCell align="right">Variable</TableCell>
              <TableCell align="right">Contribución / u.</TableCell>
              <TableCell align="right">Margen / u.</TableCell>
              <TableCell align="right">u. por hora</TableCell>
              <TableCell sx={{ minWidth: 180 }}>Contribución por hora de máquina</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(p => (
              <TableRow key={p.productId} hover>
                <TableCell sx={{ fontWeight: 650 }}>
                  {p.productName}
                  {p.machines.length > 0 && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{p.machines.join(', ')}</Typography>}
                </TableCell>
                <TableCell align="right">{p.units ? fmtQty(p.units) : <Typography component="span" variant="body2" color="text.secondary">sin ventas</Typography>}</TableCell>
                <TableCell align="right">{fmtMoney2(p.avgPrice)}{!p.units && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>de lista</Typography>}</TableCell>
                <TableCell align="right">{p.variableUnitCost != null ? fmtMoney2(p.variableUnitCost) : '—'}</TableCell>
                <TableCell align="right"><StatusPill tone={toneOf(p.contributionUnit)}>{p.contributionUnit != null ? fmtMoney2(p.contributionUnit) : 'sin costeo'}</StatusPill></TableCell>
                <TableCell align="right">
                  {p.marginUnit != null ? <StatusPill tone={toneOf(p.marginUnit)}>{fmtMoney2(p.marginUnit)} · {pct(p.marginPct, 0)}</StatusPill> : '—'}
                </TableCell>
                <TableCell align="right">{p.unitsPerHourReal != null ? fmtQty(p.unitsPerHourReal) : p.unitsPerHourStandard != null ? `${fmtQty(p.unitsPerHourStandard)} (std)` : '—'}</TableCell>
                <TableCell>
                  {p.contributionPerHour != null ? (
                    <Stack spacing={0.4}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 750, color: p.contributionPerHour < 0 ? TONES.bad.fg : INK }}>{fmtMoney(p.contributionPerHour)} / h</Typography>
                      <Box sx={{ height: 6, borderRadius: 3, bgcolor: '#F1F0ED', overflow: 'hidden' }}>
                        <motion.div style={{ height: '100%', borderRadius: 3, background: p.contributionPerHour < 0 ? TONES.bad.dot : ACCENT }}
                          initial={{ width: 0 }} animate={{ width: `${Math.max(0, p.contributionPerHour / maxH) * 100}%` }} transition={{ duration: 0.7 }} />
                      </Box>
                    </Stack>
                  ) : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Section>
  );
};

// ==================== Simulador de cambio de matriz ====================

const SETUP_KEY = 'factory.moldChangeHours';

const MoldSimulator: React.FC<{ results: FactoryResults | null }> = ({ results }) => {
  const options = (results?.products ?? []).filter(p => p.contributionPerHour != null);
  const [fromId, setFromId] = useState<number | ''>('');
  const [toId, setToId] = useState<number | ''>('');
  const [setupHours, setSetupHours] = useState(() => { try { return localStorage.getItem(SETUP_KEY) || '4'; } catch { return '4'; } });
  const [extraCost, setExtraCost] = useState('');
  const [order, setOrder] = useState('');
  useEffect(() => { try { localStorage.setItem(SETUP_KEY, setupHours); } catch { /* sin storage */ } }, [setupHours]);
  useEffect(() => {
    if (options.length >= 2 && fromId === '' && toId === '') { setFromId(options[options.length - 1].productId); setToId(options[0].productId); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results]);

  const a = options.find(p => p.productId === fromId);
  const b = options.find(p => p.productId === toId);
  const uph = (p?: ProductProfit) => p?.unitsPerHourReal ?? p?.unitsPerHourStandard ?? 0;
  const calc = useMemo(() => {
    if (!a || !b) return null;
    const h = num(setupHours);
    const lost = h * (a.contributionPerHour ?? 0);
    const setupCost = Math.max(0, lost) + num(extraCost);
    const delta = (b.contributionPerHour ?? 0) - (a.contributionPerHour ?? 0);
    const breakEvenHours = delta > 0 ? setupCost / delta : null;
    const breakEvenUnits = breakEvenHours != null ? breakEvenHours * uph(b) : null;
    const units = num(order);
    const runHours = units > 0 && uph(b) > 0 ? units / uph(b) : null;
    const gain = runHours != null ? delta * runHours - setupCost : null;
    return { setupCost, delta, breakEvenHours, breakEvenUnits, runHours, gain };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [a, b, setupHours, extraCost, order]);

  let verdict: { tone: Tone; text: string } | null = null;
  if (a && b && calc) {
    if (a.productId === b.productId) verdict = { tone: 'neutral', text: 'Elegí dos productos distintos.' };
    else if (calc.delta <= 0) verdict = { tone: 'bad', text: `Por plata no conviene: ${b.productName} deja ${fmtMoney(Math.abs(calc.delta))} menos por hora que ${a.productName}. Cambiá solo si hace falta para cumplir un pedido o porque se termina el stock.` };
    else if (calc.gain != null) verdict = calc.gain > 0
      ? { tone: 'ok', text: `Conviene: con ${fmtQty(num(order))} u. de ${b.productName} la máquina corre ${fmtQty(calc.runHours!)} h y gana ${fmtMoney(calc.gain)} más que si seguía con ${a.productName}, ya descontado el cambio.` }
      : { tone: 'warn', text: `Para ${fmtQty(num(order))} u. no conviene: el cambio cuesta ${fmtMoney(calc.setupCost)} y la corrida no llega a recuperarlo (pierde ${fmtMoney(Math.abs(calc.gain))}). Juntá un pedido de al menos ${fmtQty(calc.breakEvenUnits ?? 0)} u.` };
    else verdict = { tone: 'ok', text: `Conviene si vas a correr ${b.productName} más de ${fmtQty(calc.breakEvenHours ?? 0)} h (unas ${fmtQty(calc.breakEvenUnits ?? 0)} u.). Por debajo de eso, el cambio de matriz se come la diferencia.` };
  }

  return (
    <Section title="¿Conviene cambiar la matriz?" description="Cambiar de matriz para la máquina: esas horas no se produce y se pierde lo que dejaba el producto que estaba. Conviene si el producto nuevo deja más por hora y la corrida es lo bastante larga para recuperar el cambio. Los fijos no cuentan: se pagan igual con o sin cambio.">
      {options.length < 2 ? <EmptyState title="Hacen falta al menos dos productos con costeo y precio" /> : (
        <Box sx={{ p: 2.5 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(5, 1fr)' }, gap: 1.5 }}>
            <TextField select size="small" label="Hoy está haciendo" value={fromId} onChange={e => setFromId(Number(e.target.value))}>
              {options.map(p => <MenuItem key={p.productId} value={p.productId}>{p.productName}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Pasar a" value={toId} onChange={e => setToId(Number(e.target.value))}>
              {options.map(p => <MenuItem key={p.productId} value={p.productId}>{p.productName}</MenuItem>)}
            </TextField>
            <TextField size="small" label="Horas del cambio" value={setupHours} onChange={e => setSetupHours(e.target.value)} helperText="parada + ajuste hasta la primera pieza buena" />
            <MoneyField label="Otros costos del cambio" value={extraCost} onChange={setExtraCost} hint="material de purga, mecánico externo" />
            <TextField size="small" label="Unidades a producir" value={order} onChange={e => setOrder(e.target.value)} helperText="opcional: el pedido a cumplir" />
          </Box>
          {a && b && calc && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 1.5, mt: 2 }}>
              <Mini label={`${a.productName}: deja por hora`} value={fmtMoney(a.contributionPerHour ?? 0)} />
              <Mini label={`${b.productName}: deja por hora`} value={fmtMoney(b.contributionPerHour ?? 0)} />
              <Mini label="Costo del cambio" value={fmtMoney(calc.setupCost)} />
              <Mini label="Corrida mínima para recuperarlo" value={calc.breakEvenHours != null ? `${fmtQty(calc.breakEvenHours)} h · ${fmtQty(calc.breakEvenUnits ?? 0)} u.` : 'no se recupera'} />
            </Box>
          )}
          {verdict && <Alert severity={verdict.tone === 'ok' ? 'success' : verdict.tone === 'bad' ? 'error' : verdict.tone === 'warn' ? 'warning' : 'info'} sx={{ mt: 2 }}>{verdict.text}</Alert>}
        </Box>
      )}
    </Section>
  );
};

const Mini: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ bgcolor: '#F7F6F3', borderRadius: 2, px: 1.75, py: 1.25 }}>
    <Typography sx={{ fontSize: 12.5, color: MUTED }}>{label}</Typography>
    <Typography sx={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.02em' }}>{value}</Typography>
  </Box>
);

export default FactoryCostClosePage;
